import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import Button from "@components/Button";
import { BottomSheet } from "@rapid/components/BottomSheet";
import { TopHint } from "@rapid/components/TopHint";
import { useFrame } from "@rapid/contexts/FrameContext";
import { useModel } from "@rapid/contexts/ModelContext";
import { useCoreApp } from "@rapid/contexts/CoreAppContext";
import { useFlow } from "@rapid/contexts/FlowContext";
import { v4 as uuidv4 } from "uuid";
import { getLabelColor } from "@rapid/components/annotation/colors";
import type { Annotation } from "@rapid/types/annotation";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@rapid/analytics/types";
import colors from "tailwindcss/colors";
import {
    useCurrentMedia,
    useCurrentMediaFileRenderedDimensions,
    useMediaStore
} from "@rapid/store";
import { useMediaUtils } from "@rapid/hooks/useMediaUtils";

const NewObjectBottomSheet = ({
    isOpen,
    setIsOpen,
    onClose
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onClose: () => void;
}) => {
    const [objectName, setObjectName] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    // Use optimized hooks instead of useFlow()
    const currentMedia = useCurrentMedia();
    const currentMediaFileRenderedDimensions = useCurrentMediaFileRenderedDimensions();

    const { setSelectedAnnotationId } = useFrame();
    const { setCurrentFrameAnnotations } = useFlow(); // Keep for frame annotation setter
    const { promptLabels } = useModel();
    const { track } = useAnalytics();

    const handleCloseModal = useCallback(() => {
        setIsOpen(false);
        setObjectName(""); // Clear the input when closing
        onClose();
    }, [setIsOpen, setObjectName, onClose]);

    const handleSubmit = useCallback(() => {
        const labelName = objectName?.trim?.()?.toLowerCase?.() ?? "";

        if (!labelName || labelName.length === 0) {
            return;
        }

        // Track new object addition
        track({
            event: AnalyticsFlowEvent.NEW_OBJECT_ADDED,
            data: {
                appSource: AppSource.RAPID,
                objectName: labelName,
                creationMethod: "bottom_sheet",
                canvasDimensions: {
                    width: currentMediaFileRenderedDimensions?.width || 400,
                    height: currentMediaFileRenderedDimensions?.height || 300
                },
                annotationPosition: {
                    x: (currentMediaFileRenderedDimensions?.width || 400) / 2,
                    y: (currentMediaFileRenderedDimensions?.height || 300) / 2
                }
            }
        });

        // Create a 50x50 bounding box in the center of the canvas
        const canvasWidth = currentMediaFileRenderedDimensions?.width || 400; // fallback width
        const canvasHeight = currentMediaFileRenderedDimensions?.height || 300; // fallback height

        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2;

        // TODO: validate currentFrameId - should this be the frame uuid (BE expects this?)

        const labelColor = getLabelColor(promptLabels, labelName);

        const newAnnotation: Annotation = {
            id: uuidv4(),
            x: centerX,
            y: centerY,
            width: 100,
            height: 100,
            label: labelName,
            frameId: currentMedia?.frame?.id!,
            color: labelColor,
            draft: true,
            fileId: currentMedia?.file.id!
        };

        // Add the annotation to the current frame
        setCurrentFrameAnnotations((prev: Annotation[]) => [
            ...prev,
            { ...newAnnotation, modifiedAt: Date.now() }
        ]);

        // Select the newly created annotation (selectedObject will be computed from this)
        setSelectedAnnotationId(newAnnotation.id);

        handleCloseModal();
    }, [
        objectName,
        track,
        currentMediaFileRenderedDimensions,
        currentMedia?.file.id,
        currentMedia?.frame?.id,
        setCurrentFrameAnnotations,
        setSelectedAnnotationId,
        handleCloseModal,
        promptLabels
    ]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
            } else if (e.key === "Escape") {
                handleCloseModal();
            }
        },
        [handleSubmit, handleCloseModal]
    );

    const focusInput = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            // Clear the input when opening
            setObjectName("");

            // Focus immediately - mobile browsers need this to happen in direct response to user interaction
            if (inputRef.current) {
                focusInput();
            }

            // Also try after animation delays as backup
            setTimeout(focusInput, 100);
            setTimeout(focusInput, 300);
        }
    }, [isOpen, focusInput]);

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={handleCloseModal}
            theme="dark"
            showCloseButton={false}
        >
            <div className="flex gap-2 bg-gray-800 p-4">
                <input
                    ref={inputRef}
                    type="text"
                    value={objectName}
                    onChange={(e) => setObjectName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter object name"
                    className="w-full rounded-lg bg-gray-700 px-2 py-1 text-base text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none focus:ring-0"
                    inputMode="text"
                />
                <Button type="dark-ghost" onClick={handleSubmit} disabled={!objectName}>
                    Done
                </Button>
            </div>
        </BottomSheet>
    );
};

const NewObjectInstructions = ({ onNewObjectClick }: { onNewObjectClick: () => void }) => {
    return (
        <div className="flex w-full flex-col gap-2 p-3 md:hidden">
            <p className="text-xs text-primary">
                Tell us what objects you want to find by clicking "New Object" below.
            </p>
            <Button className="w-full" type="secondary" onClick={onNewObjectClick}>
                <i className="far fa-plus" />
                New Object
            </Button>
        </div>
    );
};

const DenseObjectCard = ({
    objectName,
    count,
    onClick,
    isSelected
}: {
    objectName: string;
    count: number;
    onClick: () => void;
    isSelected: boolean;
}) => {
    const { promptLabels } = useModel();
    const labelColor = getLabelColor(promptLabels, objectName);

    return (
        <div
            className="flex w-full gap-1 rounded-lg border p-2"
            onClick={onClick}
            style={{ borderColor: isSelected ? labelColor : colors.gray[300] }}
        >
            <div className="flex items-center gap-2">
                <div
                    className="h-3 w-3 rounded-full border-1 border-gray-300"
                    style={{ backgroundColor: labelColor }}
                />
                <p className="text-sm font-medium text-primary">{objectName}</p>
                <span className="font-base rounded-lg bg-gray-100 px-2 py-1 text-xs text-primary">
                    {count}
                </span>
            </div>
        </div>
    );
};

const UnselectedObjectSection = ({ onNewObjectClick }: { onNewObjectClick: () => void }) => {
    // Use optimized hooks
    const { flowLocked } = useFlow();

    const { annotationsForFile } = useModel();
    const { setSelectedAnnotationId } = useFrame();
    const { selectedObject } = useCoreApp();

    const handleObjectClick = useCallback(
        (annotationId: string) => {
            setSelectedAnnotationId(annotationId);
        },
        [setSelectedAnnotationId]
    );

    if (flowLocked) return null;

    const allAnnotations = Object.values(annotationsForFile)
        .flat()
        .filter((anno) => !anno.draft);

    const objects = Array.from(
        new Set(
            (Array.isArray(allAnnotations) ? allAnnotations : [])
                .map((anno) => anno?.label)
                .filter((label): label is string => typeof label === "string")
        )
    );

    const objectsProps = objects.map((object) => {
        const count = allAnnotations.filter((anno) => anno?.label === object).length;
        const objectAnnotations = allAnnotations.filter((anno) => anno?.label === object);
        const firstObjectAnnotation = objectAnnotations[0];
        return {
            objectName: object,
            count,
            onClick: () => {
                if (firstObjectAnnotation) {
                    handleObjectClick(firstObjectAnnotation.id);
                }
            }
        };
    });

    return (
        <div className="flex w-full flex-col gap-2">
            <NewObjectInstructions onNewObjectClick={onNewObjectClick} />
            <div className="flex flex-col gap-2 p-3">
                {objectsProps.map((object) => (
                    <DenseObjectCard
                        key={object.objectName}
                        {...object}
                        onClick={object.onClick}
                        isSelected={Boolean(selectedObject && object.objectName === selectedObject)}
                    />
                ))}
            </div>
        </div>
    );
};

const SelectedObjectSection = () => {
    const pendingDeleteRef = useRef<{ annotationId: string; frameId: string } | null>(null);

    // Use optimized hooks
    const { selectedObject } = useCoreApp();
    const { annotationsForFile, promptLabels } = useModel();
    const { setSelectedAnnotationId } = useFrame();
    const { setCurrentFrameAnnotations } = useFlow(); // Keep for frame annotation setter
    const { track } = useAnalytics();
    const media = useMediaStore();
    const { setCurrentMedia } = useMediaUtils();
    const currentMedia = useCurrentMedia();
    const currentMediaFileRenderedDimensions = useCurrentMediaFileRenderedDimensions();

    // Function to directly add a new annotation of the same type
    const handleAddSameObjectType = useCallback(() => {
        if (!selectedObject || !currentMediaFileRenderedDimensions) return;

        // Track new object addition
        track({
            event: AnalyticsFlowEvent.NEW_OBJECT_ADDED,
            data: {
                appSource: AppSource.RAPID,
                objectName: selectedObject,
                creationMethod: "add_same_type",
                canvasDimensions: {
                    width: currentMediaFileRenderedDimensions.width || 400,
                    height: currentMediaFileRenderedDimensions.height || 300
                },
                annotationPosition: {
                    x: (currentMediaFileRenderedDimensions.width || 400) / 2,
                    y: (currentMediaFileRenderedDimensions.height || 300) / 2
                }
            }
        });

        // Create a 50x50 bounding box in the center of the canvas
        const canvasWidth = currentMediaFileRenderedDimensions.width || 400;
        const canvasHeight = currentMediaFileRenderedDimensions.height || 300;
        const centerX = canvasWidth / 2;
        const centerY = canvasHeight / 2;

        const labelColor = getLabelColor(promptLabels, selectedObject);

        const newAnnotation: Annotation = {
            id: uuidv4(),
            x: centerX,
            y: centerY,
            width: 50,
            height: 50,
            label: selectedObject,
            frameId: currentMedia?.frame?.id!,
            color: labelColor,
            draft: true,
            fileId: currentMedia?.file.id!
        };

        // Add the annotation to the current frame
        setCurrentFrameAnnotations((prev: Annotation[]) => [
            ...prev,
            { ...newAnnotation, modifiedAt: Date.now() }
        ]);

        // Select the newly created annotation
        setSelectedAnnotationId(newAnnotation.id);
    }, [
        selectedObject,
        currentMediaFileRenderedDimensions,
        track,
        currentMedia?.file.id,
        currentMedia?.frame?.id,
        setCurrentFrameAnnotations,
        setSelectedAnnotationId,
        promptLabels
    ]);

    const {
        allAnnotations,
        annotationsForThisObject,
        count,
        annotationObjectsForThumbnails,
        hasDraftAnnotation
    } = useMemo(() => {
        const allAnnotations = Object.values(annotationsForFile).flat();

        const annotationsForThisObject = Array.isArray(allAnnotations)
            ? allAnnotations.filter((anno) => anno?.label === selectedObject)
            : [];
        const count = annotationsForThisObject.length;

        const annotationObjectsForThumbnails = annotationsForThisObject.map((anno) => {
            if (!anno.thumbnailDataUrl) return null;
            return { ...anno, thumbnailDataUrl: anno.thumbnailDataUrl };
        });

        const hasDraftAnnotation = allAnnotations.some((anno) => anno.draft);

        return {
            allAnnotations,
            annotationsForThisObject,
            count,
            annotationObjectsForThumbnails,
            hasDraftAnnotation
        };
    }, [annotationsForFile, selectedObject]);

    const handleThumbnailClick = useCallback(
        (e: React.MouseEvent, annotation: Annotation) => {
            e.stopPropagation(); // Prevent click from bubbling to parent div
            const targetMediaItem = media[annotation.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[annotation.frameId]
                : targetMediaItem;

            if (
                !annotation ||
                !annotation.frameId ||
                !annotation.id ||
                !targetMediaItem ||
                !targetFrame
            ) {
                console.warn("Clicked annotation thumbnail is missing frameId or id", annotation);
                return;
            }

            // 1. Set current frame in context
            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            // 2. Set selected annotation ID in context
            setSelectedAnnotationId(annotation.id);

            // Video seeking will be handled by FilePreview reacting to frame changes
        },
        [media, setCurrentMedia, setSelectedAnnotationId]
    );

    const handleDeleteAnnotationClick = useCallback(
        (e: React.MouseEvent, annotationToDelete: Annotation) => {
            e.preventDefault(); // Prevent default button behavior
            e.stopPropagation(); // Important: Stop event from bubbling to parent elements

            if (!annotationToDelete || !annotationToDelete.frameId || !annotationToDelete.id) {
                console.warn(
                    "Attempted to delete annotation missing frameId or id",
                    annotationToDelete
                );
                return;
            }

            // Track annotation deletion from thumbnail
            track({
                event: AnalyticsFlowEvent.ANNOTATION_OBJECT_DELETED,
                data: {
                    appSource: AppSource.RAPID,
                    source: "canvas",
                    deletionMethod: "thumbnail_click",
                    annotationId: annotationToDelete.id,
                    annotationLabel: annotationToDelete.label,
                    frameId: annotationToDelete.frameId,
                    fileId: annotationToDelete.fileId,
                    remainingAnnotations: annotationsForThisObject.length - 1
                }
            });

            const copyAnnotations = [...annotationsForThisObject];

            // If we're already on the correct frame, delete immediately
            if (annotationToDelete.frameId === currentMedia?.frame?.id) {
                setCurrentFrameAnnotations((prevFrameAnnotations: Annotation[]) => {
                    if (!prevFrameAnnotations) return [];
                    return prevFrameAnnotations.filter((anno) => anno.id !== annotationToDelete.id);
                });
            } else {
                // For different frame (video case), use the pending delete approach
                pendingDeleteRef.current = {
                    annotationId: annotationToDelete.id,
                    frameId: annotationToDelete.frameId
                };

                const targetMediaItem = media[annotationToDelete.fileId];
                const targetFrame = targetMediaItem.isVideo
                    ? media[annotationToDelete.frameId]
                    : targetMediaItem;

                // Navigate to the annotation's frame
                setCurrentMedia({
                    file: targetMediaItem,
                    frame: targetFrame,
                    frameSegment: targetFrame.metadata?.segment!
                });
            }

            if (copyAnnotations.length === 1) {
                setSelectedAnnotationId(null);
            } else {
                const newSelectedAnnotationId = copyAnnotations.find(
                    (anno) => anno.id !== annotationToDelete.id
                )?.id;
                if (newSelectedAnnotationId) {
                    setSelectedAnnotationId(newSelectedAnnotationId);
                }
            }
        },
        [
            media,
            track,
            annotationsForThisObject,
            currentMedia?.frame?.id,
            setCurrentFrameAnnotations,
            setCurrentMedia,
            setSelectedAnnotationId
        ]
    );

    // Effect to handle actual deletion once frame navigation is complete
    useEffect(() => {
        const pendingDelete = pendingDeleteRef.current;
        if (pendingDelete && currentMedia?.frame?.id === pendingDelete.frameId) {
            setCurrentFrameAnnotations((prevFrameAnnotations: Annotation[]) => {
                if (!prevFrameAnnotations) return [];
                return prevFrameAnnotations.filter(
                    (anno) => anno.id !== pendingDelete.annotationId
                );
            });
            pendingDeleteRef.current = null; // Reset pending delete ref
        }
    }, [currentMedia?.frame?.id, setCurrentFrameAnnotations]);

    return (
        <motion.div
            className="relative flex w-full flex-col gap-2 border-t-1 border-gray-600 bg-gray-800 p-3 pb-40"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
        >
            {/* title */}
            <div className="flex items-center gap-2">
                <div
                    className="h-3 w-3 rounded-full border-1 border-gray-600"
                    style={{ backgroundColor: getLabelColor(promptLabels, selectedObject!) }}
                />
                <p className="text-sm font-medium text-white">{selectedObject}</p>
                <span className="font-base rounded-lg bg-gray-700 px-2 py-1 text-xs text-white">
                    {count}
                </span>
            </div>
            {/* + button and annotation thumbs */}
            <div className="flex w-full items-start gap-2">
                {/* Add new annotation button */}
                <button
                    onClick={handleAddSameObjectType}
                    disabled={hasDraftAnnotation}
                    className={`flex h-16 w-16 flex-shrink-0 items-center justify-center rounded border-2 border-dashed transition-colors ${
                        hasDraftAnnotation
                            ? "cursor-not-allowed border-gray-600 text-gray-600"
                            : "border-gray-500 text-gray-400 hover:border-gray-400 hover:text-gray-300"
                    }`}
                    aria-label={`Add new ${selectedObject} annotation`}
                >
                    <i className="far fa-plus text-xl" />
                </button>

                {/* Existing annotation thumbnails */}
                <div className="flex flex-wrap gap-2">
                    {annotationObjectsForThumbnails.map(
                        (anno, index) =>
                            anno &&
                            anno.thumbnailDataUrl && (
                                <div
                                    key={`${selectedObject}-thumb-wrapper-${index}-${anno.id}`}
                                    className="group relative h-16 w-16 cursor-pointer rounded border border-gray-200"
                                    onClick={(e) => handleThumbnailClick(e, anno as Annotation)}
                                >
                                    <img
                                        src={anno.thumbnailDataUrl}
                                        alt={`${selectedObject} annotation thumbnail ${index + 1}`}
                                        className="h-full w-full rounded object-cover"
                                    />
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDeleteAnnotationClick(e, anno as Annotation);
                                        }}
                                        className="absolute right-0 top-0 z-10 m-0.5 flex h-4 w-4 -translate-y-2 translate-x-2 items-center justify-center rounded-full bg-white p-0.5 text-xs leading-none text-gray-500 focus:outline-none"
                                        aria-label="Delete annotation"
                                    >
                                        <i className="far fa-close" />
                                    </button>
                                </div>
                            )
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export const PromptVisualAreaBottomContent = () => {
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

    // Use optimized hooks
    const { setHideSidebar, selectedObject } = useCoreApp();
    const { setSelectedAnnotationId } = useFrame();

    const handleScreenChange = useCallback(
        (e: MediaQueryListEvent | MediaQueryList) => {
            // Hide sidebar on mobile (below md), show on desktop (md and up)
            setHideSidebar(!e.matches);
        },
        [setHideSidebar]
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia("(min-width: 768px)"); // md breakpoint

        // Set initial state
        handleScreenChange(mediaQuery);

        // Listen for changes
        mediaQuery.addEventListener("change", handleScreenChange);

        return () => {
            mediaQuery.removeEventListener("change", handleScreenChange);
        };
    }, [handleScreenChange]);

    const handleOpenBottomSheet = useCallback(() => {
        setIsBottomSheetOpen(true);
        setHideSidebar(true);

        // Try to focus immediately in response to user click
        // This helps with mobile browser restrictions on programmatic focus
        setTimeout(() => {
            const input = document.querySelector(
                'input[placeholder="Enter object name"]'
            ) as HTMLInputElement;
            if (input) {
                input.focus();
                input.select();
            }
        }, 50);
    }, [setHideSidebar]);

    const handleCloseBottomSheet = useCallback(() => {
        setIsBottomSheetOpen(false);
    }, []);

    const showGrayBackgroundFill = isBottomSheetOpen;

    return (
        <>
            {/* Hint that opens/closes with the bottom sheet */}
            <TopHint
                isVisible={isBottomSheetOpen}
                message="Name object"
                onClose={handleCloseBottomSheet}
                theme="dark"
                className="md:hidden"
            />

            {/* Hint that opens/closes when editing an object */}
            <TopHint
                isVisible={!!selectedObject}
                message={`Move the box over the ${selectedObject?.toLowerCase?.()}`}
                showCloseButton={false}
                onSubmit={() => setSelectedAnnotationId(null)}
                theme="dark"
                className="md:hidden"
            />

            {/* Gray background fill when bottom sheet is open or model is generating */}
            <motion.div
                className="relative w-full bg-gray-900 md:hidden"
                initial={{ height: 0 }}
                animate={{
                    height: showGrayBackgroundFill ? "auto" : 0,
                    opacity: showGrayBackgroundFill ? 1 : 0
                }}
                transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                    // Start showing background slightly before content disappears
                    delay: showGrayBackgroundFill ? 0 : 0.15
                }}
                style={{
                    overflow: "hidden",
                    // Give it some minimum height when showing to fill the space
                    minHeight: showGrayBackgroundFill ? "700px" : "0",
                    marginTop: showGrayBackgroundFill ? "-2px" : "0"
                }}
            />

            <motion.div
                className="relative flex w-full flex-col gap-2 md:hidden"
                initial={false}
                animate={{
                    height: isBottomSheetOpen ? 0 : "auto",
                    opacity: isBottomSheetOpen ? 0 : 1
                }}
                transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                    // Hide content slightly faster so background can show
                    delay: isBottomSheetOpen ? 0.1 : 0
                }}
                style={{ overflow: "hidden" }}
            >
                {selectedObject ? (
                    <SelectedObjectSection />
                ) : (
                    <UnselectedObjectSection onNewObjectClick={handleOpenBottomSheet} />
                )}
            </motion.div>

            <NewObjectBottomSheet
                isOpen={isBottomSheetOpen}
                setIsOpen={setIsBottomSheetOpen}
                onClose={handleCloseBottomSheet}
            />
        </>
    );
};
